import { useSyncExternalStore } from "react";

export type FeatureToggle = { id: string; name: string };

export const FEATURE_TOGGLES = [
  { id: "performance-drivers", name: "Performance Drivers" },
] as const satisfies readonly FeatureToggle[];

export type FeatureToggleId = (typeof FEATURE_TOGGLES)[number]["id"];

const keyPrefix = "feature-toggle:";
const storageKey = (id: FeatureToggleId) => `${keyPrefix}${id}`;

const listeners = new Set<() => void>();

// A toggle is off unless the browser holds "on" for it. A browser that refuses
// to read storage reads every toggle as off.
export function readFeatureToggle(id: FeatureToggleId): boolean {
  try {
    return window.localStorage.getItem(storageKey(id)) === "on";
  } catch {
    return false;
  }
}

export function writeFeatureToggle(id: FeatureToggleId, on: boolean) {
  try {
    if (on) window.localStorage.setItem(storageKey(id), "on");
    else window.localStorage.removeItem(storageKey(id));
  } catch {
    // Storage can be unavailable; the toggle then reads as off.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  // The storage event fires in other tabs only; same-tab writes notify above.
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith(keyPrefix)) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function useFeatureToggle(id: FeatureToggleId): boolean {
  return useSyncExternalStore(
    subscribe,
    () => readFeatureToggle(id),
    () => false,
  );
}
